/**
 * Layer rendering utilities for Cytoscape.js
 *
 * Provides rendering functions for different visualization layers
 * using cytoscape-layers and cytoscape-overlays plugins.
 */

import type { Core, NodeSingular, EdgeSingular, BoundingBox12, BoundingBoxWH } from 'cytoscape';
import type { LayersPlugin } from 'cytoscape-layers';
import type { LayerType, ThroughputData, ErrorData, LinkSpeed, PhysicalData } from '../types/layers';

// VLAN color palette for up to 16 VLANs
const VLAN_COLORS = [
  '#3273dc', '#48c774', '#ffdd57', '#f14668',
  '#00d1b2', '#b86bff', '#ff8c00', '#00bcd4',
  '#9c27b0', '#4caf50', '#ff5722', '#607d8b',
  '#e91e63', '#8bc34a', '#ffc107', '#795548',
];

// Physical link speed colors (categorical, distinct)
const LINK_SPEED_COLORS: Record<LinkSpeed, string> = {
  '10m': '#C0392B',   // Dark Red - very slow
  '100m': '#E67E22',  // Burnt Orange
  '1g': '#16A085',    // Deep Teal
  '10g': '#6C5CE7',   // Indigo
  'unknown': '#95A5A6', // Gray for unknown speeds
};

/**
 * Get color for a link speed category
 */
export function getLinkSpeedColor(speed: LinkSpeed): string {
  return LINK_SPEED_COLORS[speed] || LINK_SPEED_COLORS['unknown'];
}

/**
 * Get human-readable label for link speed
 */
export function getLinkSpeedLabel(speed: LinkSpeed): string {
  switch (speed) {
    case '10m': return '10 Mbps';
    case '100m': return '100 Mbps';
    case '1g': return '1 Gbps';
    case '10g': return '10 Gbps';
    default: return 'Unknown';
  }
}

// Wireless endpoint-edge colors by radio band (Physical lens only).
// Picked from the same "flat UI" family as LINK_SPEED_COLORS, but in
// hues the speed palette doesn't already use, so a band swatch never
// reads as a mislabeled speed swatch: amber for 2.4GHz, blue for
// 5GHz, violet for 6GHz. `band` is a free string from the backend, so
// this is intentionally a lookup that can miss — unknown/absent bands
// fall back to the neutral dashed default already in the base
// stylesheet rather than forcing a color.
const WIRELESS_BAND_COLORS: Record<string, string> = {
  '2.4GHz': '#F39C12', // Amber — distinct from the 100m Carrot orange (#E67E22)
  '5GHz': '#2980B9',   // Belize blue — distinct from Teal (1g) and Indigo (10g)
  '6GHz': '#8E44AD',   // Wisteria violet — distinct from the 10g/LAG indigo (#6C5CE7)
};

/**
 * Get the color for a wireless radio band, or `undefined` for an
 * unknown/absent band. Callers should leave the edge at its neutral
 * default in the `undefined` case rather than picking a fallback
 * color — an unrecognized band is "we don't know," not "gray band."
 */
export function getWirelessBandColor(band: string | undefined | null): string | undefined {
  if (!band) return undefined;
  return WIRELESS_BAND_COLORS[band];
}

/**
 * Structural input for the wireless endpoint-edge tooltip label —
 * kept separate from `EdgeSingular` so tests can supply hand-built
 * fixtures without a real Cytoscape instance (same rationale as
 * `StpOverlayLayerInput` above).
 */
export interface WirelessEdgeTooltipData {
  ssid?: string;
  band?: string;
  channel?: number;
  signalDbm?: number;
}

/**
 * Build the Physical-lens hover label for a wireless endpoint edge,
 * e.g. "CorpNet — 5GHz ch 36, -62 dBm". Any field the AP/backend
 * didn't report is dropped rather than shown as blank or "unknown".
 */
export function getWirelessTooltipLabel(data: WirelessEdgeTooltipData): string {
  const { ssid, band, channel, signalDbm } = data;

  let bandChannel = '';
  if (band && channel !== undefined) {
    bandChannel = `${band} ch ${String(channel)}`;
  } else if (band) {
    bandChannel = band;
  } else if (channel !== undefined) {
    bandChannel = `ch ${String(channel)}`;
  }

  const signalPart = signalDbm !== undefined ? `${String(signalDbm)} dBm` : '';
  const detail = [bandChannel, signalPart].filter((part) => part.length > 0).join(', ');

  if (ssid && detail) return `${ssid} — ${detail}`;
  if (ssid) return ssid;
  if (detail) return detail;
  return 'Wireless';
}

/**
 * Get a consistent color for a VLAN ID
 */
export function getVlanColor(vlanId: number): string {
  return VLAN_COLORS[vlanId % VLAN_COLORS.length];
}

/**
 * Format throughput value to human-readable string
 */
export function formatThroughput(bps: number): string {
  if (bps >= 1e9) return `${(bps / 1e9).toFixed(1)} Gbps`;
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(1)} Mbps`;
  if (bps >= 1e3) return `${(bps / 1e3).toFixed(1)} Kbps`;
  return `${String(bps)} bps`;
}

/**
 * Get utilization color based on percentage.
 *
 * `null` means the link speed is unknown, not that the link is idle, and it
 * gets a neutral gray rather than the healthy green those ports used to be
 * painted. More than half of production interfaces land here.
 */
export function getUtilizationColor(utilization: number | null): string {
  if (utilization === null) return '#b5b5b5'; // unknown — no denominator
  if (utilization >= 90) return '#f14668'; // danger red
  if (utilization >= 70) return '#ffdd57'; // warning yellow
  if (utilization >= 50) return '#00d1b2'; // info cyan
  return '#48c774'; // success green
}

/**
 * The worse of a link's two directions, or `null` when neither end has a known
 * speed. `Math.max` cannot be used directly: it coerces `null` to 0, which
 * turns "unknown" into "idle" — the exact conflation this change removes.
 */
export function maxUtilization(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return Math.max(a, b);
}

interface LayerCleanup {
  remove: () => void;
}

/**
 * Interface VLAN info from endpoint data
 */
interface EndpointVlanInfo {
  vlanId: number;
  isTagged?: boolean;
  isNative?: boolean;
}

/**
 * Check if an edge is a trunk (multiple VLANs or has tagged VLANs)
 */
function isTrunkLink(edge: EdgeSingular): boolean {
  const vlans = edge.data('vlans') as number[] | undefined;
  if (!vlans || vlans.length === 0) return false;
  if (vlans.length > 1) return true;

  // Check if the single VLAN is tagged on either endpoint
  const sourceEndpoint = edge.data('sourceEndpoint') as { vlans?: EndpointVlanInfo[] } | undefined;
  const targetEndpoint = edge.data('targetEndpoint') as { vlans?: EndpointVlanInfo[] } | undefined;

  const hasTagged = (endpoint: { vlans?: EndpointVlanInfo[] } | undefined) => {
    return endpoint?.vlans?.some((v) => v.isTagged) ?? false;
  };

  return hasTagged(sourceEndpoint) || hasTagged(targetEndpoint);
}

/**
 * Check if a VLAN is native (untagged) on an edge
 */
function isVlanNativeOnEdge(edge: EdgeSingular, vlanId: number): boolean {
  const sourceEndpoint = edge.data('sourceEndpoint') as { vlans?: EndpointVlanInfo[] } | undefined;
  const targetEndpoint = edge.data('targetEndpoint') as { vlans?: EndpointVlanInfo[] } | undefined;

  const isNativeOnEndpoint = (endpoint: { vlans?: EndpointVlanInfo[] } | undefined) => {
    const vlan = endpoint?.vlans?.find((v) => v.vlanId === vlanId);
    return vlan?.isNative ?? false;
  };

  // Native if untagged on either endpoint
  return isNativeOnEndpoint(sourceEndpoint) || isNativeOnEndpoint(targetEndpoint);
}

/**
 * Render VLAN overlay layer
 *
 * Default view (no VLAN selected):
 * - Access links (single untagged VLAN): thin solid gray line
 * - Trunk links (multiple VLANs or tagged): thicker dashed gray line
 *
 * With VLAN selected:
 * - Edges carrying selected VLAN: colored (solid if native, dashed if tagged)
 * - Edges not carrying selected VLAN: dimmed gray
 */
export function renderVlanLayer(
  cy: Core,
  layers: LayersPlugin,
  selectedVlanId: number | null = null
): LayerCleanup {
  // Hide base edges by making them fully transparent — but leave
  // endpoint overlay edges (`endpointLink`) alone. Those are styled
  // by the base stylesheet, not this canvas layer (see the
  // `{ selector: 'edge[^endpointLink]' }` scoping below), so hiding
  // them here would make satellite links vanish while VLAN is active.
  cy.edges('[^endpointLink]').style('line-opacity', 0);

  // Insert edge canvas below nodes
  const edgeCanvasLayer = layers.nodeLayer.insertBefore('canvas');

  // Render edges based on trunk/access or selected VLAN
  const edgeResult = layers.renderPerEdge(
    edgeCanvasLayer,
    (ctx: CanvasRenderingContext2D, edge: EdgeSingular, path: Path2D) => {
      const vlans = edge.data('vlans') as number[] | undefined;
      const hasVlans = vlans && vlans.length > 0;
      const isTrunk = isTrunkLink(edge);

      if (selectedVlanId !== null) {
        // VLAN filter mode: highlight edges carrying the selected VLAN
        const carriesSelectedVlan = vlans?.includes(selectedVlanId) ?? false;

        if (carriesSelectedVlan) {
          // Edge carries the selected VLAN - color it
          const isNative = isVlanNativeOnEdge(edge, selectedVlanId);
          ctx.strokeStyle = getVlanColor(selectedVlanId);
          ctx.lineWidth = 4;
          ctx.lineCap = 'round';

          if (isNative) {
            // Native/untagged - solid line
            ctx.setLineDash([]);
          } else {
            // Tagged - dashed line (longer gaps for visibility)
            ctx.setLineDash([10, 8]);
          }
          ctx.stroke(path);
          ctx.setLineDash([]);
        } else {
          // Edge does not carry selected VLAN - dimmed
          ctx.strokeStyle = '#d5d5d5';
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.setLineDash([]);
          ctx.stroke(path);
        }
      } else {
        // Default view: show trunk vs access distinction
        if (!hasVlans) {
          // No VLANs known - neutral gray
          ctx.strokeStyle = '#a0a0a0';
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.setLineDash([]);
          ctx.stroke(path);
        } else if (isTrunk) {
          // Trunk link - thicker dashed line (longer gaps for visibility)
          ctx.strokeStyle = '#7a7a7a';
          ctx.lineWidth = 4;
          ctx.lineCap = 'round';
          ctx.setLineDash([10, 8]);
          ctx.stroke(path);
          ctx.setLineDash([]);
        } else {
          // Access link - thin solid line
          ctx.strokeStyle = '#7a7a7a';
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.setLineDash([]);
          ctx.stroke(path);
        }
      }
    },
    // Excludes endpoint overlay edges — they keep their own neutral/
    // dashed styling from the base stylesheet instead of being
    // repainted gray by the VLAN canvas layer.
    { selector: 'edge[^endpointLink]' }
  );

  return {
    remove: () => {
      edgeResult.remove();
      edgeCanvasLayer.remove();
      // Restore base edge visibility (only what we hid above)
      cy.edges('[^endpointLink]').style('line-opacity', 1);
    }
  };
}

// STP layer colors. Picked to stay within the established palette
// (success/danger/info/warning) so the visualization reads the same
// way as the rest of the app.
const STP_FORWARDING_COLOR = '#48c774';      // success green — both sides forwarding
const STP_BLOCKED_COLOR = '#7a7a7a';         // neutral grey — STP correctly blocking redundancy
const STP_CONFLICT_COLOR = '#f14668';        // danger red — asymmetric state, real problem
const STP_NO_DATA_COLOR = '#d5d5d5';         // very light grey — no STP info available
const STP_VARIANT_MISMATCH_COLOR = '#ff8c00'; // orange dashed — endpoints running different protocols
const STP_FOCUS_NEAR_COLOR = '#3273dc';      // info blue — selected device's view of its end
const STP_ROOT_BRIDGE_GLOW = '#48c774';      // success green underlay on root bridges
const TC_GLOW_COLOR = '#ffdd57';             // warning amber overlay on recently-flapped devices
const TC_GLOW_DECAY_SECONDS = 1800;          // 30 min — full opacity at 0s, zero by 30 min

/**
 * Classify the link state from one side's port_state. STP port states
 * collapse to two buckets for the topology view: "forwarding" (the
 * link carries traffic) and "not forwarding" (the link is up but
 * blocked, learning, discarding, etc — STP says "do not use").
 *
 * Returns `null` when the side has no port_state at all (missing
 * data), which the caller treats differently from "we know it's
 * blocked."
 */
type LinkSide = 'forwarding' | 'not-forwarding' | null;
function classifyLinkSide(portState: string | null): LinkSide {
  if (!portState) return null;
  return portState.toLowerCase() === 'forwarding' ? 'forwarding' : 'not-forwarding';
}

/** A port_role that legitimately implies "I'm intentionally not
 *  forwarding traffic, by design." When the other end of the link
 *  reports forwarding, that's not a fault — it's RSTP/MSTP doing
 *  its job of suppressing a redundant path. The "winning" end is the
 *  designated bridge for the segment and forwards; the "losing" end
 *  picks alternate/backup and blocks. Each end knows its own role;
 *  neither expects the other to mirror it.
 *
 *  - `alternate`: a redundant path to the root via a different port
 *    on a different bridge; blocks until the primary fails.
 *  - `backup`: a redundant path on the SAME bridge (multiple ports
 *    in the same segment); blocks all but one.
 *  - `disabled`: admin-down or not in the spanning tree.
 *
 *  `root` and `designated` are NOT legit-blocked roles — a root or
 *  designated port that isn't forwarding is the actual problem.
 */
function isLegitimatelyBlocked(role: string | null): boolean {
  if (!role) return false;
  const r = role.toLowerCase();
  return r === 'alternate' || r === 'backup' || r === 'disabled';
}

/**
 * Render the STP layer.
 *
 * Two coloring modes determined by `focusDeviceId`:
 *
 *   - **Consensus** (focusDeviceId === null): each edge resolves to
 *     ONE of four colors from both sides' agreement on the link
 *     state — green if both forwarding, grey-dashed if STP is
 *     correctly suppressing a redundant path (either both blocking,
 *     OR one side is in `alternate`/`backup`/`disabled` role — STP
 *     deciding the link is the backup; the other end doesn't have
 *     to mirror the role), red if asymmetric WITHOUT a legit-block
 *     role explaining it (BPDUs aren't flowing one direction, or
 *     the not-forwarding side claims root/designated), light grey
 *     if data is missing on either side.
 *
 *   - **Single-device** (focusDeviceId set): the edge color comes
 *     from the focus device's view of its own port. The
 *     unselected end is rendered as a half-line dim grey so the
 *     operator can click the other end to compare. Lets you walk
 *     both endpoints of a flapping link and see each side's
 *     opinion.
 *
 * Root bridges for the selected instance get a green underlay ring
 * via the existing Cytoscape node-class mechanism (not via the
 * canvas — keeps the styling consistent with how `node:selected`
 * already renders its highlight).
 *
 * When the overlay data or instance selection is missing we render
 * every edge with `STP_NO_DATA_COLOR` and skip the root-bridge ring —
 * the layer is still active so the user can change the instance
 * selector without re-toggling layers.
 */
export function renderStpLayer(
  cy: Core,
  layers: LayersPlugin,
  overlay: StpOverlayLayerInput | null,
  selectedInstance: { instanceId: number; instanceType: string } | null,
  focusDeviceId: string | null
): LayerCleanup {
  // Match an instance record against the selected tuple. Both the
  // numeric id AND the protocol variant must match — instance_id 0
  // isn't unique across variants, so filtering on the id alone
  // mixes RSTP and MSTP-CIST rows together.
  const matchesSelected = (
    sel: { instanceId: number; instanceType: string } | null,
    instanceId: number,
    instanceType: string
  ): boolean => {
    if (sel === null) return false;
    return sel.instanceId === instanceId && sel.instanceType === instanceType;
  };

  // Index edge states by (connectionId, instanceId, instanceType)
  // for O(1) lookup during the draw callback. The connectionId
  // matches the edge id the topology endpoint already uses.
  type EdgeKey = string;
  const edgeKey = (cid: string, inst: number, typ: string): EdgeKey =>
    `${cid}::${String(inst)}::${typ}`;
  const edgeByKey = new Map<EdgeKey, StpOverlayLayerInput['edgeStates'][number]>();
  if (overlay) {
    for (const es of overlay.edgeStates) {
      edgeByKey.set(edgeKey(es.connectionId, es.instanceId, es.instanceType), es);
    }
  }

  // Devices that are the root bridge for the selected instance. Used
  // for the underlay-ring highlight; recomputed below if the instance
  // changes.
  const rootDeviceIds = new Set<string>();
  if (overlay && selectedInstance !== null) {
    for (const ds of overlay.deviceStates) {
      if (matchesSelected(selectedInstance, ds.instanceId, ds.instanceType) && ds.isRootBridge) {
        rootDeviceIds.add(ds.deviceId);
      }
    }
  }

  // Per-device protocol variant. A device runs one STP variant at a
  // time (verified by the audit — variant_mismatch on a single device
  // would be a separate finding). When two endpoints of an edge run
  // DIFFERENT variants, comparing their port states is meaningless
  // (RSTP and MSTP encode "forwarding" differently, can't agree, can't
  // disagree). The visualization treats this as a separate "variant
  // mismatch" state, color-coded distinctly so the operator sees it
  // as a real problem rather than just no-data grey.
  //
  // Multiple deviceStates per device are allowed (MSTP devices have
  // one row per MSTI), but all rows for a single device share the
  // same instance_type — first-write-wins is fine.
  const deviceVariant = new Map<string, string>();
  if (overlay) {
    for (const ds of overlay.deviceStates) {
      if (!deviceVariant.has(ds.deviceId)) {
        deviceVariant.set(ds.deviceId, ds.instanceType);
      }
    }
  }
  // Variant-compatibility matrix. The IEEE 802.1D / 802.1w (RSTP) /
  // 802.1s (MSTP) family all share BPDU format and interoperate
  // (MSTP-CIST falls back to RSTP at boundaries, RSTP falls back to
  // legacy STP). The real wire-incompatibility is Cisco's
  // PVST+/Rapid-PVST+ vs the IEEE-standard family — PVST uses a
  // proprietary BPDU encoding that the others don't decode.
  //
  // So "cross-variant" for visualization purposes is only true when
  // crossing the PVST boundary, not within the IEEE family. Lumping
  // RSTP↔MSTP into "mismatch" would paint every real-world hybrid
  // deployment as a fault, which is wrong.
  const IEEE_FAMILY = new Set(['stp', 'rstp', 'mstp']);
  const PVST_FAMILY = new Set(['pvst', 'rapid_pvst', 'rapid-pvst', 'pvst+']);
  const variantFamily = (v: string): 'ieee' | 'pvst' | 'unknown' => {
    const lower = v.toLowerCase();
    if (IEEE_FAMILY.has(lower)) return 'ieee';
    if (PVST_FAMILY.has(lower)) return 'pvst';
    return 'unknown';
  };
  const isCrossVariantEdge = (sourceId: string, targetId: string): boolean => {
    const sv = deviceVariant.get(sourceId);
    const tv = deviceVariant.get(targetId);
    if (sv === undefined || tv === undefined) return false;
    const sf = variantFamily(sv);
    const tf = variantFamily(tv);
    // Unknown families fall through to "not a known mismatch" — we'd
    // rather show the existing per-instance rendering than paint
    // every edge orange when we can't tell what's running.
    if (sf === 'unknown' || tf === 'unknown') return false;
    return sf !== tf;
  };

  // Highlight root-bridge nodes via direct inline style. Putting it
  // in the global stylesheet would require mutating cy.style() which
  // has awkward chaining semantics; per-node inline style is simpler
  // and reverts cleanly on layer cleanup.
  const styledRootNodeIds: string[] = [];
  rootDeviceIds.forEach((id) => {
    const node = cy.getElementById(id);
    if (node.length > 0) {
      node.style({
        'underlay-color': STP_ROOT_BRIDGE_GLOW,
        'underlay-padding': '11px',
        'underlay-opacity': 0.55,
        'underlay-shape': 'ellipse',
      });
      styledRootNodeIds.push(id);
    }
  });

  // Recent-topology-change glow. Honest v1 — the glow lives on the
  // *device* node, not on an edge, because the gateway's
  // TopologyChangeEvent stream carries an instance_id but not the
  // implicated port (most MIBs don't expose it; `dot1dStpTopChangePort`
  // exists in Cisco's extensions but isn't collected yet). When that
  // data lands later, this same renderer can pivot to a per-edge
  // glow without restructuring.
  //
  // Alpha decays linearly from 1.0 at the moment of the TC to 0.0
  // over TC_GLOW_DECAY_SECONDS. The full window matches roughly two
  // gateway poll cycles plus the SaaS audit cycle — enough time for
  // an operator scanning the View to notice the glow before it
  // fades out.
  //
  // Uses cytoscape's `overlay-*` style (vs root bridge's
  // `underlay-*`) so the two halos coexist — a recently-changed
  // root bridge shows both rings simultaneously.
  const styledTcGlowNodeIds: string[] = [];
  if (overlay && selectedInstance !== null) {
    const nowMs = Date.now();
    for (const ds of overlay.deviceStates) {
      if (!matchesSelected(selectedInstance, ds.instanceId, ds.instanceType)) continue;
      if (!ds.lastTopologyChange) continue;
      const tcMs = Date.parse(ds.lastTopologyChange);
      if (Number.isNaN(tcMs)) continue;
      const secondsSinceTc = (nowMs - tcMs) / 1000;
      if (secondsSinceTc < 0) continue; // future timestamp, defensive
      const alpha = Math.max(0, 1 - secondsSinceTc / TC_GLOW_DECAY_SECONDS);
      if (alpha <= 0.01) continue; // skip fully-faded — nothing to draw
      const node = cy.getElementById(ds.deviceId);
      if (node.length > 0) {
        node.style({
          'overlay-color': TC_GLOW_COLOR,
          'overlay-padding': '14px',
          'overlay-opacity': alpha * 0.65, // 0.65 cap so it's visible without nuking the icon
          'overlay-shape': 'ellipse',
        });
        styledTcGlowNodeIds.push(ds.deviceId);
      }
    }
  }

  // Hide base edges by making them fully transparent — same trick the
  // VLAN layer uses so our canvas-drawn lines are the only visible
  // representation. Endpoint overlay edges are excluded (see the
  // `edge[^endpointLink]` scoping below) so they keep their own
  // stylesheet-driven look while STP is active.
  cy.edges('[^endpointLink]').style('line-opacity', 0);

  const edgeCanvasLayer = layers.nodeLayer.insertBefore('canvas');
  const edgeResult = layers.renderPerEdge(
    edgeCanvasLayer,
    (ctx: CanvasRenderingContext2D, edge: EdgeSingular, path: Path2D) => {
      if (!overlay || selectedInstance === null) {
        // Layer is on but no instance selected (or no overlay data) —
        // render every edge as no-data so the user gets visible
        // feedback that the layer is active.
        ctx.strokeStyle = STP_NO_DATA_COLOR;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.setLineDash([]);
        ctx.stroke(path);
        return;
      }

      const srcId = edge.source().id();
      const tgtId = edge.target().id();

      // Variant-mismatch only intercepts in CONSENSUS mode. In
      // single-device mode the operator is asking "what does this
      // one device think about its links" — that question has a
      // sensible answer even when the far end runs a different
      // protocol (the focus device's own port_state is still
      // meaningful). Painting variant-mismatch orange there would
      // hide the focus device's view, which is the whole point of
      // clicking the node.
      if (focusDeviceId === null && isCrossVariantEdge(srcId, tgtId)) {
        ctx.strokeStyle = STP_VARIANT_MISMATCH_COLOR;
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.setLineDash([10, 6]);
        ctx.stroke(path);
        ctx.setLineDash([]);
        return;
      }

      // Resolve which variant to look up by. In CONSENSUS mode it's
      // whatever the legend has selected. In SINGLE-DEVICE mode the
      // user clicked a specific device to see ITS view — we should
      // always render the focus device's actual variant, even if the
      // legend has a different one selected. Otherwise picking MST0
      // and then clicking an STP-only device silently grays out all
      // edges because the (id, mstp) key doesn't match the focus's
      // (id, stp) rows. The instance_id still applies — relevant for
      // MSTP devices with multiple MSTIs.
      const lookupVariant: string = focusDeviceId
        ? (deviceVariant.get(focusDeviceId) ?? selectedInstance.instanceType)
        : selectedInstance.instanceType;
      const state = edgeByKey.get(
        edgeKey(edge.id(), selectedInstance.instanceId, lookupVariant)
      );
      if (!state) {
        // No STP data for this edge × instance combination. Edge is
        // in the topology but neither endpoint reports STP for it.
        ctx.strokeStyle = STP_NO_DATA_COLOR;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.setLineDash([]);
        ctx.stroke(path);
        return;
      }

      if (focusDeviceId === null) {
        // CONSENSUS MODE
        const src = classifyLinkSide(state.sourcePortState);
        const tgt = classifyLinkSide(state.targetPortState);

        if (src === null || tgt === null) {
          // Missing on one side — render neutral. Could be an edge
          // port (admin_edge_port=true → the other end is a host
          // and won't report STP) or just partial data.
          ctx.strokeStyle = STP_NO_DATA_COLOR;
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.setLineDash([]);
          ctx.stroke(path);
        } else if (src === 'forwarding' && tgt === 'forwarding') {
          // Both forwarding → active link in the spanning tree.
          ctx.strokeStyle = STP_FORWARDING_COLOR;
          ctx.lineWidth = 4;
          ctx.lineCap = 'round';
          ctx.setLineDash([]);
          ctx.stroke(path);
        } else if (src === 'not-forwarding' && tgt === 'not-forwarding') {
          // Both blocking → STP correctly suppressing a redundant
          // path. Rendered grey-dashed so the operator sees it's a
          // valid backup link, not a fault.
          ctx.strokeStyle = STP_BLOCKED_COLOR;
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.setLineDash([8, 6]);
          ctx.stroke(path);
          ctx.setLineDash([]);
        } else {
          // Asymmetric port_state — one side forwarding, the other
          // not. Could be one of two very different things:
          //
          // (a) Legitimate redundancy: the blocking side has
          //     port_role = alternate/backup/disabled, meaning it
          //     KNOWS it's the redundant path and is correctly
          //     suppressing itself. The forwarding side has no
          //     reason to think anything is wrong — each end of an
          //     RSTP/MSTP link decides its own role independently.
          //     This is STP doing its job; render as blocked.
          //
          // (b) Actual conflict: BPDUs aren't flowing one direction,
          //     or one side has root/designated role but isn't
          //     forwarding (the role demands forwarding), or roles
          //     are missing entirely. Risk of black-hole / loop;
          //     render red.
          const blockedRole = src === 'not-forwarding' ? state.sourcePortRole : state.targetPortRole;
          if (isLegitimatelyBlocked(blockedRole)) {
            ctx.strokeStyle = STP_BLOCKED_COLOR;
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.setLineDash([8, 6]);
            ctx.stroke(path);
            ctx.setLineDash([]);
          } else {
            ctx.strokeStyle = STP_CONFLICT_COLOR;
            ctx.lineWidth = 5;
            ctx.lineCap = 'round';
            ctx.setLineDash([]);
            ctx.stroke(path);
          }
        }
        return;
      }

      // SINGLE-DEVICE MODE — color from focus device's view of its
      // own end. Render the link as one half-color from the focus
      // device's perspective, the other half dimmed grey. Lets the
      // operator click either endpoint to compare each side's view
      // of the same link.
      let focusSide: LinkSide;
      let focusIsSource: boolean;
      if (state.sourceDeviceId === focusDeviceId) {
        focusSide = classifyLinkSide(state.sourcePortState);
        focusIsSource = true;
      } else if (state.targetDeviceId === focusDeviceId) {
        focusSide = classifyLinkSide(state.targetPortState);
        focusIsSource = false;
      } else {
        // Focus device isn't an endpoint of this edge — render as
        // background-dimmed so it visually recedes from the
        // focused subgraph.
        ctx.strokeStyle = STP_NO_DATA_COLOR;
        ctx.lineWidth = 1.5;
        ctx.lineCap = 'round';
        ctx.setLineDash([]);
        ctx.stroke(path);
        return;
      }

      const focusColor = focusSide === 'forwarding'
        ? STP_FORWARDING_COLOR
        : focusSide === 'not-forwarding'
        ? STP_BLOCKED_COLOR
        : STP_NO_DATA_COLOR;

      // Render in two passes against the SAME Path2D that
      // cytoscape provided — this is the actual bezier curve, so
      // both passes follow it pixel-for-pixel and there's no
      // straight-line drift across curved edges. The geometry trick
      // is a half-plane clip on the second pass: stroke the whole
      // curve in grey first (so the far half ends up grey), then
      // clip to the near half-plane and overstroke in the focus
      // color (so the near half ends up focus-colored).
      const srcPos = edge.source().position();
      const tgtPos = edge.target().position();
      const nearX = focusIsSource ? srcPos.x : tgtPos.x;
      const nearY = focusIsSource ? srcPos.y : tgtPos.y;
      const midX = (srcPos.x + tgtPos.x) / 2;
      const midY = (srcPos.y + tgtPos.y) / 2;
      const dx = nearX - midX;
      const dy = nearY - midY;
      const len = Math.hypot(dx, dy);

      if (len < 1) {
        // Source and target collapsed onto each other (self-loop
        // or degenerate edge). The half-plane is undefined; just
        // paint everything in the focus color and skip the clip
        // trick entirely.
        ctx.strokeStyle = focusColor;
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.setLineDash([]);
        ctx.stroke(path);
      } else {
        // Pass 1 — full bezier in grey (far-half color).
        ctx.strokeStyle = STP_NO_DATA_COLOR;
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.setLineDash([]);
        ctx.stroke(path);

        // Pass 2 — clip to the half-plane on the near side of the
        // perpendicular bisector, then overstroke in the focus
        // color. The clip polygon is a giant rectangle anchored at
        // the midpoint, with one edge along the perpendicular
        // bisector and extending `BIG` units toward the near
        // endpoint. `BIG` just has to exceed any plausible canvas
        // size; we don't need pixel precision because the bezier
        // is well-contained between the endpoints.
        const ux = dx / len;          // unit vector toward near
        const uy = dy / len;
        const vx = -uy;               // perpendicular
        const vy = ux;
        const BIG = 10000;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(midX + vx * BIG, midY + vy * BIG);
        ctx.lineTo(midX - vx * BIG, midY - vy * BIG);
        ctx.lineTo(midX - vx * BIG + ux * BIG * 2, midY - vy * BIG + uy * BIG * 2);
        ctx.lineTo(midX + vx * BIG + ux * BIG * 2, midY + vy * BIG + uy * BIG * 2);
        ctx.closePath();
        ctx.clip();

        ctx.strokeStyle = focusColor;
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.stroke(path);
        ctx.restore();
      }

      // Highlight the focus device's near-side endpoint so it's
      // visually clear which side this color is coming from.
      ctx.fillStyle = STP_FOCUS_NEAR_COLOR;
      ctx.beginPath();
      ctx.arc(nearX, nearY, 6, 0, Math.PI * 2);
      ctx.fill();
    },
    // Excludes endpoint overlay edges — STP data never applies to
    // them, and drawing over them would clobber their neutral/dashed
    // stylesheet look.
    { selector: 'edge[^endpointLink]' }
  );

  return {
    remove: () => {
      edgeResult.remove();
      edgeCanvasLayer.remove();
      cy.edges('[^endpointLink]').style('line-opacity', 1);
      // Strip the inline underlay style we pushed onto the root
      // bridge nodes so the next layer doesn't inherit it.
      styledRootNodeIds.forEach((id) => {
        const node = cy.getElementById(id);
        if (node.length > 0) {
          node.removeStyle('underlay-color underlay-padding underlay-opacity underlay-shape');
        }
      });
      // Same for the TC glow overlay — strip the inline style so a
      // layer-switch doesn't leave amber rings around recently-
      // changed devices when STP isn't being shown anymore.
      styledTcGlowNodeIds.forEach((id) => {
        const node = cy.getElementById(id);
        if (node.length > 0) {
          node.removeStyle('overlay-color overlay-padding overlay-opacity overlay-shape');
        }
      });
    },
  };
}

/**
 * Reads a design token off the document, for a canvas that cannot use CSS.
 *
 * The layer this replaced hardcoded `#ffffff` boxes and `#dbdbdb` borders, so
 * its chrome was light-mode whatever the reader had chosen. Anything drawn here
 * comes from the same tokens the rest of the page uses.
 */
function token(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value === '' ? fallback : value;
}

/** Thinnest and thickest an edge is ever drawn. */
const MIN_EDGE_WIDTH = 2.5;
const MAX_EDGE_WIDTH = 9;

/**
 * The decade the width scale tops out at, and the one it starts from.
 *
 * Anchored to fixed values rather than to the visible set's own maximum, which
 * is the difference between "this link is busy" and "this link is the busiest
 * thing currently on screen". Panning to a quieter corner must not make its
 * links look like uplinks.
 */
const WIDTH_FLOOR_BPS = 1e3;
const WIDTH_CEILING_BPS = 1e10;

/**
 * Edge width from absolute rate, log-scaled.
 *
 * Width carries magnitude because magnitude needs no denominator and is honest
 * on every device — unlike utilization, which more than half the fleet cannot
 * compute at all.
 */
function edgeWidth(bitsPerSecond: number): number {
  if (!(bitsPerSecond > 0)) return MIN_EDGE_WIDTH;
  const span = Math.log10(WIDTH_CEILING_BPS) - Math.log10(WIDTH_FLOOR_BPS);
  const position = (Math.log10(Math.max(bitsPerSecond, WIDTH_FLOOR_BPS)) - Math.log10(WIDTH_FLOOR_BPS)) / span;
  return MIN_EDGE_WIDTH + (MAX_EDGE_WIDTH - MIN_EDGE_WIDTH) * Math.min(1, Math.max(0, position));
}

/** Why an edge is drawn as needing attention, or `null` when it is not. */
type EdgeTrouble = 'loss' | 'saturated';

function edgeTrouble(throughput: {
  inbound: ThroughputData;
  outbound: ThroughputData;
  errors?: ErrorData;
}): EdgeTrouble | null {
  if (throughput.errors?.hasErrors === true) return 'loss';
  // Only where a speed actually resolved. `maxUtilization` keeps `null` out of
  // `Math.max`, which would otherwise turn "unknown" into "idle".
  const util = maxUtilization(throughput.inbound.utilization, throughput.outbound.utilization);
  return util !== null && util >= 99 ? 'saturated' : null;
}

/** A teardrop, for frames that left and did not arrive. */
function drawDroplet(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.bezierCurveTo(x + r, y - r * 0.2, x + r * 0.75, y + r, x, y + r);
  ctx.bezierCurveTo(x - r * 0.75, y + r, x - r, y - r * 0.2, x, y - r);
  ctx.closePath();
  ctx.fill();
}

/** A dial with its needle at the top of the scale, for a link at its rail. */
function drawGauge(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, stroke: string) {
  ctx.beginPath();
  ctx.arc(x, y, r, Math.PI * 0.85, Math.PI * 0.15, false);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = stroke;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + r * 0.7, y - r * 0.5);
  ctx.stroke();
}

/**
 * The traffic lens: width is how much, colour is whether to look.
 *
 * What this replaced spent three channels — width, colour and a printed
 * percentage — on one quantity, and that quantity was fabricated: capacity fell
 * back to a hardcoded gigabit, so a saturated 100M port read 10% and most of
 * the fleet painted a confident healthy green. Capacity is nullable now and
 * there is nothing left to feed that ramp.
 *
 * So: width carries magnitude, which needs no denominator and is honest on
 * every device. Colour carries emphasis and has two states, not five. **Nothing
 * is painted green for being fine** — the absence of the attention colour is
 * the healthy state, it costs no ink, and it makes no claim the data cannot
 * support.
 *
 * The per-edge percentage label, the node sparkline and the node utilization
 * bar are all gone. A number on every edge is a number on every data point, and
 * the sparkline's chrome was hardcoded light-mode besides.
 */
export function renderThroughputLayer(
  cy: Core,
  layers: LayersPlugin
): LayerCleanup {
  // Hide base edges by making them fully transparent — endpoint
  // overlay edges are excluded (see the `edge[^endpointLink]` scoping
  // below) so they keep their own stylesheet-driven look instead of
  // being hidden and then repainted gray as "no throughput data".
  cy.edges('[^endpointLink]').style('line-opacity', 0);

  const ink = token('--fg-subtle', '#7a7a7a');
  const attention = token('--sev-1', '#e5484d');
  const card = token('--card', '#ffffff');

  const edgeCanvasLayer = layers.nodeLayer.insertBefore('canvas');
  const markerCanvasLayer = layers.nodeLayer.insertBefore('canvas');

  const edgeResult = layers.renderPerEdge(
    edgeCanvasLayer,
    (ctx: CanvasRenderingContext2D, edge: EdgeSingular, path: Path2D) => {
      const throughput = edge.data('throughput') as { inbound: ThroughputData; outbound: ThroughputData; errors?: ErrorData } | undefined;

      if (!throughput) {
        // The link exists and we know nothing about it. Drawn at the thinnest
        // width in muted ink rather than hidden: absent data is not absent
        // cable.
        ctx.strokeStyle = ink;
        ctx.globalAlpha = 0.35;
        ctx.lineWidth = MIN_EDGE_WIDTH;
        ctx.lineCap = 'round';
        ctx.stroke(path);
        ctx.globalAlpha = 1;
        return;
      }

      const bits = Math.max(throughput.inbound.current, throughput.outbound.current);
      const trouble = edgeTrouble(throughput);

      ctx.strokeStyle = trouble === null ? ink : attention;
      ctx.globalAlpha = trouble === null ? 0.75 : 1;
      ctx.lineWidth = edgeWidth(bits);
      ctx.lineCap = 'round';
      ctx.stroke(path);
      ctx.globalAlpha = 1;
    },
    // Excludes endpoint overlay edges — they never carry `throughput`
    // data, so without this scoping every satellite link would get
    // the "no data" treatment drawn over its own styling.
    { selector: 'edge[^endpointLink]' }
  );

  // Markers ride above every edge so one is never drawn under a neighbour.
  const markerResult = layers.renderPerEdge(
    markerCanvasLayer,
    (ctx: CanvasRenderingContext2D, edge: EdgeSingular) => {
      const throughput = edge.data('throughput') as { inbound: ThroughputData; outbound: ThroughputData; errors?: ErrorData } | undefined;
      if (!throughput) return;
      const trouble = edgeTrouble(throughput);
      // Healthy links get no marker at all. An icon on every edge is an icon on
      // no edge.
      if (trouble === null) return;

      const { x, y } = edge.midpoint();
      // A disc in the surface colour behind the mark, so it stays legible where
      // edges cross.
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.fillStyle = card;
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = attention;
      ctx.stroke();

      ctx.fillStyle = attention;
      if (trouble === 'loss') {
        drawDroplet(ctx, x, y, 4.5);
      } else {
        drawGauge(ctx, x, y + 2, 4.5, attention);
      }
    },
    { selector: 'edge[throughput]' }
  );

  return {
    remove: () => {
      edgeResult.remove();
      markerResult.remove();
      edgeCanvasLayer.remove();
      markerCanvasLayer.remove();
      cy.edges('[^endpointLink]').style('line-opacity', 1);
    },
  };
}

/**
 * Per-device finding summary passed into the findings renderer.
 * Mirrors `DeviceFindingSummary` from useTopologyFindings as a
 * structural type so the renderer doesn't import from the hook
 * file (avoids cyclic risk and lets tests build fixtures by hand).
 */
export interface FindingsLayerDeviceSummary {
  deviceId: string;
  count: number;
  /** "critical" | "high" | "medium" | "low" | "info" — drives badge color. */
  worstSeverity: string;
}

/**
 * Render the generic findings overlay — severity-coded badges on
 * device nodes whose incidents are active.
 *
 * This is a SECONDARY canvas layer that runs alongside whatever
 * primary layer (topology, VLAN, STP, etc.) the user has selected.
 * It mounts when the View loads and stays mounted across layer
 * switches; the cleanup hook removes its canvas only when the
 * region changes or the View unmounts.
 *
 * Designed generic: any audit that upserts into `incidents` with a
 * device_id gains the badge automatically. The STP audit is the
 * first consumer but nothing in this renderer is STP-aware.
 *
 * v1 limitations:
 *   - Node badges only. Edge badges are positioned for when the
 *     `incidents.interface_id` column lands; until then the layer
 *     just doesn't draw on edges.
 *   - No interactive tooltips. The badge is purely visual; the
 *     details live in the device detail panel which the existing
 *     node-click handler opens. Click the node and look at the
 *     incidents tab.
 */
export function renderFindingsLayer(
  cy: Core,
  layers: LayersPlugin,
  summariesByDevice: Map<string, FindingsLayerDeviceSummary>,
  severityColorFn: (severity: string) => string,
  severityNeedsDarkTextFn: (severity: string) => boolean
): LayerCleanup {
  // Layer placed ABOVE nodes (`append`) so the badge sits on top of
  // the icon, not behind it. Using `append` rather than
  // `nodeLayer.insertAfter` makes the z-order explicit.
  const canvasLayer = layers.append('canvas');

  const nodeResult = layers.renderPerNode(
    canvasLayer,
    (ctx: CanvasRenderingContext2D, node: NodeSingular, bb: BoundingBox12 & BoundingBoxWH) => {
      const summary = summariesByDevice.get(node.id());
      if (!summary || summary.count === 0) return;

      // Badge position — outside the top-right of the node bbox so
      // it isn't obscured by the node icon. OSPF places its state
      // indicator at `bb.w + 6, 6`; this lifts mine a bit further
      // out (+9) because the badge is larger than OSPF's dot.
      const badgeCenterX = bb.w + 9;
      const badgeCenterY = -3;
      const radius = summary.count >= 10 ? 11 : 9;

      const fill = severityColorFn(summary.worstSeverity);
      const textColor = severityNeedsDarkTextFn(summary.worstSeverity) ? '#363636' : '#ffffff';

      // Drop shadow for visibility against any node icon. Set
      // shadow on the path then clear after — `save/restore` would
      // also work but this is cheaper for the common case.
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
      ctx.shadowBlur = 4;
      ctx.shadowOffsetY = 1;
      ctx.fillStyle = fill;
      ctx.beginPath();
      ctx.arc(badgeCenterX, badgeCenterY, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // White outline so the badge stays legible on dark or icon-
      // colored backgrounds.
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(badgeCenterX, badgeCenterY, radius, 0, Math.PI * 2);
      ctx.stroke();

      // Count. Showing "9+" once we hit double digits keeps the
      // badge compact; the device detail panel has the full list.
      const label = summary.count > 9 ? '9+' : String(summary.count);
      ctx.fillStyle = textColor;
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, badgeCenterX, badgeCenterY + 0.5);
    },
    { selector: 'node' }
  );

  // Wake the cy renderer when summaries change so the badge layer
  // repaints — without this, cy doesn't know the underlying data
  // shifted and the badge stays stale until the user pans/zooms.
  cy.emit('render');

  return {
    remove: () => {
      nodeResult.remove();
      canvasLayer.remove();
    },
  };
}

/**
 * Render OSPF overlay layer
 * Shows OSPF areas, neighbor states, and link costs
 */
export function renderOspfLayer(
  _cy: Core,
  layers: LayersPlugin
): LayerCleanup {
  const canvasLayer = layers.append('canvas');

  // OSPF area colors
  const areaColors: Record<string, string> = {};
  let colorIndex = 0;

  function getAreaColor(areaId: string): string {
    if (!areaColors[areaId]) {
      areaColors[areaId] = VLAN_COLORS[colorIndex % VLAN_COLORS.length];
      colorIndex++;
    }
    return areaColors[areaId];
  }

  // Neighbor state colors
  const stateColors: Record<string, string> = {
    down: '#f14668',
    init: '#ffdd57',
    'two-way': '#ffdd57',
    exstart: '#00d1b2',
    exchange: '#00d1b2',
    loading: '#3273dc',
    full: '#48c774',
  };

  // Render node OSPF information
  const nodeResult = layers.renderPerNode(
    canvasLayer,
    (ctx: CanvasRenderingContext2D, node: NodeSingular, bb: BoundingBox12 & BoundingBoxWH) => {
      const ospf = node.data('ospf') as { areaId: string; routerId: string; neighborState: string } | undefined;
      if (!ospf) return;

      // Note: The canvas context is already translated to bb.x1, bb.y1 by cytoscape-layers
      // when position='top-left' (the default), so we use relative coordinates
      const centerX = bb.w / 2;
      const topY = -12;

      // Draw area badge above node
      const areaLabel = `Area ${ospf.areaId}`;
      ctx.font = 'bold 10px sans-serif';
      const textWidth = ctx.measureText(areaLabel).width;

      // Badge background
      ctx.fillStyle = getAreaColor(ospf.areaId);
      ctx.beginPath();
      ctx.roundRect(centerX - textWidth / 2 - 4, topY - 10, textWidth + 8, 14, 3);
      ctx.fill();

      // Badge text
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(areaLabel, centerX, topY - 3);

      // State indicator dot (positioned at top-right of node)
      const stateColor = stateColors[ospf.neighborState] || '#dbdbdb';
      ctx.beginPath();
      ctx.arc(bb.w + 6, 6, 5, 0, 2 * Math.PI);
      ctx.fillStyle = stateColor;
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;
      ctx.stroke();
    },
    { selector: 'node[ospf]' }
  );

  // Render edge OSPF costs
  const edgeResult = layers.renderPerEdge(
    canvasLayer,
    (ctx: CanvasRenderingContext2D, edge: EdgeSingular, _path: Path2D, start: { x: number; y: number }, end: { x: number; y: number }) => {
      const cost = edge.data('ospfCost') as number | undefined;
      if (cost === undefined) return;

      const midX = (start.x + end.x) / 2;
      const midY = (start.y + end.y) / 2;

      // Draw cost label
      ctx.font = 'bold 11px sans-serif';
      const label = String(cost);
      const textWidth = ctx.measureText(label).width;

      // Label background
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.roundRect(midX - textWidth / 2 - 3, midY - 8, textWidth + 6, 16, 2);
      ctx.fill();
      ctx.strokeStyle = '#dbdbdb';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Label text
      ctx.fillStyle = '#363636';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, midX, midY);
    },
    { selector: 'edge[ospfCost]' }
  );

  return {
    remove: () => {
      nodeResult.remove();
      edgeResult.remove();
      canvasLayer.remove();
    }
  };
}

/**
 * Render physical layer showing link speeds
 * Colors edges by their physical speed (100Mbps, 1Gbps, 10Gbps)
 * Overlays on top of base topology edges using Cytoscape's calculated paths
 * Labels are shown only on hover
 */
export function renderPhysicalLayer(
  cy: Core,
  layers: LayersPlugin
): LayerCleanup {
  // Insert edge canvas below nodes so edges don't cover them
  const edgeCanvasLayer = layers.nodeLayer.insertBefore('canvas');
  // Append label canvas on top of everything for hover labels
  const labelCanvasLayer = layers.append('canvas');

  // Color wireless endpoint edges by radio band. Unlike the infra
  // `edge[physical]` links below (canvas-drawn), endpoint edges are
  // real Cytoscape edges already styled by the base stylesheet
  // (`edge[endpointLink = "wireless"]` — neutral, dashed). We only
  // need to recolor them, not redraw them, so a plain inline style
  // override is simplest and reverts cleanly on cleanup — same trick
  // the STP layer uses for its root-bridge node underlay. Line style
  // stays untouched (still dashed); only color and width change so
  // the color reads.
  const styledWirelessEdgeIds: string[] = [];
  cy.edges('[endpointLink = "wireless"]').forEach((edge) => {
    const band = edge.data('band') as string | undefined;
    const color = getWirelessBandColor(band);
    if (!color) return; // unknown/absent band — keep the neutral default
    edge.style({
      'line-color': color,
      'width': 2,
    });
    styledWirelessEdgeIds.push(edge.id());
  });

  // Track hovered edge for label display
  let hoveredEdgeId: string | null = null;

  // Selector shared by the hover handlers and the label canvas below —
  // one tooltip mechanism covers both infra physical links and
  // wireless endpoint edges. Wired/aggregate endpoint edges are
  // intentionally excluded (no tooltip needed).
  const TOOLTIP_SELECTOR = 'edge[physical], edge[endpointLink = "wireless"]';

  // Event handlers for hover
  const handleMouseOver = (event: { target: EdgeSingular }) => {
    hoveredEdgeId = event.target.id();
    edgeCanvasLayer.update();
    labelCanvasLayer.update();
  };

  const handleMouseOut = () => {
    hoveredEdgeId = null;
    edgeCanvasLayer.update();
    labelCanvasLayer.update();
  };

  // Register hover events
  cy.on('mouseover', TOOLTIP_SELECTOR, handleMouseOver);
  cy.on('mouseout', TOOLTIP_SELECTOR, handleMouseOut);

  // Render edges with speed-based colors using Cytoscape's calculated path
  const edgeResult = layers.renderPerEdge(
    edgeCanvasLayer,
    (ctx: CanvasRenderingContext2D, edge: EdgeSingular, path: Path2D, start: { x: number; y: number }, end: { x: number; y: number }) => {
      const physical = edge.data('physical') as PhysicalData | undefined;
      if (!physical) return;

      const color = getLinkSpeedColor(physical.speed);
      const isHovered = edge.id() === hoveredEdgeId;
      const isAggregated = edge.data('isAggregated') as boolean | undefined;

      // Draw along Cytoscape's calculated path (matches the base edge exactly)
      ctx.strokeStyle = color;
      // LAG links are thicker to indicate aggregated bandwidth
      const baseWidth = isAggregated ? 6 : 4;
      ctx.lineWidth = isHovered ? baseWidth + 2 : baseWidth;
      ctx.lineCap = 'round';
      ctx.stroke(path);

      // Draw LAG indicator badge if this is an aggregated connection
      if (isAggregated) {
        const physicalLinkCount = edge.data('physicalLinkCount') as number | undefined;
        if (physicalLinkCount && physicalLinkCount > 1) {
          const midX = (start.x + end.x) / 2;
          const midY = (start.y + end.y) / 2;

          // LAG badge background
          const badgeText = `${String(physicalLinkCount)}x`;
          ctx.font = 'bold 10px sans-serif';
          const textWidth = ctx.measureText(badgeText).width;

          ctx.fillStyle = '#6C5CE7'; // Indigo for LAG
          ctx.beginPath();
          ctx.roundRect(midX - textWidth / 2 - 6, midY - 10, textWidth + 12, 18, 9);
          ctx.fill();

          // Badge text
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(badgeText, midX, midY);
        }
      }
    },
    { selector: 'edge[physical]' }
  );

  // Render hover labels on top layer
  const labelResult = layers.renderPerEdge(
    labelCanvasLayer,
    (ctx: CanvasRenderingContext2D, edge: EdgeSingular, _path: Path2D, start: { x: number; y: number }, end: { x: number; y: number }) => {
      // Only draw label for hovered edge
      if (edge.id() !== hoveredEdgeId) return;

      const physical = edge.data('physical') as PhysicalData | undefined;
      let color: string;
      let label: string;

      if (physical) {
        color = getLinkSpeedColor(physical.speed);
        const isAggregated = edge.data('isAggregated') as boolean | undefined;
        const physicalLinkCount = edge.data('physicalLinkCount') as number | undefined;

        label = getLinkSpeedLabel(physical.speed);
        if (isAggregated && physicalLinkCount) {
          label = `LAG ${String(physicalLinkCount)}x ${label}`;
        }
      } else if (edge.data('endpointLink') === 'wireless') {
        const band = edge.data('band') as string | undefined;
        color = getWirelessBandColor(band) ?? '#95A5A6'; // neutral gray to match STP_NO_DATA/unknown-speed convention
        label = getWirelessTooltipLabel({
          ssid: edge.data('ssid') as string | undefined,
          band,
          channel: edge.data('channel') as number | undefined,
          signalDbm: edge.data('signalDbm') as number | undefined,
        });
      } else {
        return;
      }

      // Position label at edge midpoint
      const midX = (start.x + end.x) / 2;
      const midY = (start.y + end.y) / 2;

      // Calculate angle for label rotation
      const angle = Math.atan2(end.y - start.y, end.x - start.x);
      const adjustedAngle = angle > Math.PI / 2 || angle < -Math.PI / 2
        ? angle + Math.PI
        : angle;

      ctx.save();
      ctx.translate(midX, midY);
      ctx.rotate(adjustedAngle);

      // Label background
      ctx.font = 'bold 10px sans-serif';
      const textWidth = ctx.measureText(label).width;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.roundRect(-textWidth / 2 - 4, -18, textWidth + 8, 14, 3);
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Label text
      ctx.fillStyle = '#363636';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, 0, -11);

      ctx.restore();
    },
    { selector: TOOLTIP_SELECTOR }
  );

  return {
    remove: () => {
      // Remove event listeners
      cy.off('mouseover', TOOLTIP_SELECTOR, handleMouseOver);
      cy.off('mouseout', TOOLTIP_SELECTOR, handleMouseOut);
      edgeResult.remove();
      labelResult.remove();
      edgeCanvasLayer.remove();
      labelCanvasLayer.remove();
      // Revert the inline band-color override so leaving the Physical
      // lens restores the neutral dashed default from the base
      // stylesheet (mirrors how the STP layer strips its root-bridge
      // underlay style on cleanup).
      styledWirelessEdgeIds.forEach((id) => {
        const edge = cy.getElementById(id);
        if (edge.length > 0) {
          edge.removeStyle('line-color width');
        }
      });
    }
  };
}

/**
 * Layer configuration options
 */
export interface LayerOptions {
  /** Selected VLAN ID for VLAN layer filtering */
  selectedVlanId?: number | null;
  /** STP overlay data — passed straight through to renderStpLayer */
  stpOverlay?: StpOverlayLayerInput;
  /**
   * Selected STP instance for layer filtering. The tuple form is
   * required because the same instance_id is valid across multiple
   * protocol variants (RSTP-0 and MSTP-CIST both report 0) — keying
   * only on the numeric id would collapse them.
   */
  selectedStpInstance?: { instanceId: number; instanceType: string } | null;
  /**
   * When set, STP layer switches from "consensus" coloring (both
   * sides agree) to "this device's view" coloring (the unselected
   * end is dimmed). Lets the operator click either endpoint of a
   * link to see how the two sides disagree.
   */
  stpFocusDeviceId?: string | null;
}

/**
 * STP overlay payload passed into the renderer. Stays a structural
 * type so the renderer doesn't depend on the React Query hook's
 * exact return shape — keeps cyclic-import risk down and lets tests
 * supply hand-built fixtures.
 */
export interface StpOverlayLayerInput {
  deviceStates: Array<{
    deviceId: string;
    instanceId: number;
    /** Protocol variant — same rationale as on edgeStates. Required
     *  for the selection filter to distinguish RSTP-0 from
     *  MSTP-CIST. */
    instanceType: string;
    isRootBridge: boolean;
    /**
     * RFC 3339 timestamp of the most recent topology change on this
     * instance. Drives the device-level glow with alpha decay
     * (honest v1 — glow attributed to the device because current
     * TopologyChangeEvent data doesn't carry the implicated port).
     */
    lastTopologyChange: string | null;
  }>;
  edgeStates: Array<{
    connectionId: string;
    sourceDeviceId: string;
    targetDeviceId: string;
    instanceId: number;
    /** Protocol variant ("stp" / "rstp" / "mstp" / "pvst" / "rapid_pvst"
     *  / "unknown"). Selection filter must match on BOTH instanceId
     *  AND instanceType because instance_id 0 isn't unique across
     *  variants. */
    instanceType: string;
    sourcePortState: string | null;
    sourcePortRole: string | null;
    sourceAdminEdgePort: boolean | null;
    targetPortState: string | null;
    targetPortRole: string | null;
    targetAdminEdgePort: boolean | null;
  }>;
}

/**
 * Apply layer to the graph
 */
export function applyLayer(
  cy: Core,
  layers: LayersPlugin,
  layerType: LayerType,
  options: LayerOptions = {}
): LayerCleanup | null {
  switch (layerType) {
    case 'physical':
      return renderPhysicalLayer(cy, layers);
    case 'vlan':
      return renderVlanLayer(cy, layers, options.selectedVlanId ?? null);
    case 'stp':
      return renderStpLayer(
        cy,
        layers,
        options.stpOverlay ?? null,
        options.selectedStpInstance ?? null,
        options.stpFocusDeviceId ?? null
      );
    case 'throughput':
      return renderThroughputLayer(cy, layers);
    case 'ospf':
      return renderOspfLayer(cy, layers);
    case 'topology':
    default:
      // Topology is the base layer, no additional rendering needed
      return null;
  }
}

/**
 * Path overlay (S0 walking skeleton): highlight an ordered device path from
 * Path Check — thick strokes along edges between consecutive path devices,
 * rings on path nodes. Same lifecycle contract as the findings layer:
 * closure-captured data, so a data change means teardown + re-mount.
 */
export function renderPathLayer(cy: Core, layers: LayersPlugin, orderedDeviceIds: string[]): LayerCleanup {
  const pathSet = new Set(orderedDeviceIds);
  const consecutive = new Set<string>();
  for (let i = 0; i + 1 < orderedDeviceIds.length; i++) {
    const a = orderedDeviceIds[i];
    const b = orderedDeviceIds[i + 1];
    consecutive.add(`${a}|${b}`);
    consecutive.add(`${b}|${a}`);
  }

  const edgeCanvasLayer = layers.append('canvas');
  const edgeResult = layers.renderPerEdge(
    edgeCanvasLayer,
    (ctx: CanvasRenderingContext2D, edge: EdgeSingular, path: Path2D) => {
      const key = `${edge.source().id()}|${edge.target().id()}`;
      if (!consecutive.has(key)) return;
      ctx.strokeStyle = '#2a78d6';
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.stroke(path);
    }
  );

  const nodeCanvasLayer = layers.append('canvas');
  const nodeResult = layers.renderPerNode(
    nodeCanvasLayer,
    (ctx: CanvasRenderingContext2D, node: NodeSingular, bb: BoundingBox12 & BoundingBoxWH) => {
      if (!pathSet.has(node.id())) return;
      const cx = bb.w / 2;
      const cyMid = bb.h / 2;
      const radius = Math.max(bb.w, bb.h) / 2 + 7;
      ctx.strokeStyle = '#2a78d6';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(cx, cyMid, radius, 0, Math.PI * 2);
      ctx.stroke();
    },
    { selector: 'node' }
  );

  cy.emit('render');

  return {
    remove: () => {
      edgeResult.remove();
      nodeResult.remove();
      edgeCanvasLayer.remove();
      nodeCanvasLayer.remove();
    },
  };
}
